import { PackageOption } from '@shared/v2/utils/getPackageOptions';

import { SanityApi } from '@/v2/services/sanity/types';
import { Charges } from '@/v2/services/types/api/parsedResponse';
import { UI } from '@/v2/types';

export type OfferWithRoom = UI.RoomOptionOffer & {
  roomType?: UI.RoomOption['roomType'];
};

export type OtherPackageOffer = {
  leadInOffer?: SanityApi.ParsedResponse.LeadInOffer | undefined;
  offer?: OfferWithRoom; // Post-Search offer
  packageOption?: PackageOption;
};

type PostSearchPackageOptions = {
  cheapestOffer: OfferWithRoom;
  offers: Map<string, OfferWithRoom>;
};

export const offerPrice = (offer?: { charges: Charges }) =>
  offer?.charges?.total?.amount
    ? Number(offer.charges.total.amount)
    : Number.MAX_SAFE_INTEGER;

/*
  We get offers back from PAVA as an array of RoomOptions.
  Eg. [
    {
      roomType: { Deluxe.. }
      offers: [PO1_OFFER($200), PO2_OFFER($300), STANDARD_OFFER($100)..]
      ..
    },
    {
      roomType: { OceanView.. }
      offers: [PO2_OFFER($150), STANDARD_OFFER($400)..]
      ..
    }
  ]
  Our modal renders cards in terms of Cheapest per PO, not per room.
  The order being:
    1. Cheapest -> Most Expensive PO
    2. Standard Rate (if it is cheaper than the cheapest PO)

  So we parse the roomOptions to a more appropriate structure.

  In the example above, offers will be a sorted Map, where:
  [
    PO2_OFFER($150) - OceanView,
    PO1_OFFER($200) - Deluxe,
    Standard_Offer($100) - Deluxe
  ]
*/
export const getCheapestOfferPerPackageOption = (
  roomOptions?: UI.RoomOption[],
) =>
  (roomOptions ?? [])
    ?.filter((ro) => !!ro.offers)
    .reduce(
      (accum, ro) => {
        const { offers = [], roomType } = ro;

        offers.forEach((offer) => {
          const cheapestTotal = offerPrice(accum.cheapestOffer);
          const offerTotal = offerPrice(offer);

          // Store the CHEAPEST offer in ALL offers
          if (cheapestTotal > offerTotal) {
            accum.cheapestOffer = {
              ...offer,
              roomType,
            };
          }

          // Store the CHEAPEST offer PER package option
          const packageOptionId = offer.leadInOffer?.inclusionSetId;
          if (packageOptionId) {
            const currentCheapestOfferForPackageOption =
              accum.offers.get(packageOptionId);

            if (
              !currentCheapestOfferForPackageOption ||
              offerPrice(currentCheapestOfferForPackageOption) >
                offerPrice(offer)
            ) {
              accum.offers.set(packageOptionId, {
                ...offer,
                roomType,
              });
            }
          }
        });

        return accum;
      },
      {
        cheapestOffer: {},
        offers: new Map(),
      } as PostSearchPackageOptions,
    );

export const getPackageOption = (
  packageOptions: PackageOption[],
  leadInOffer?: SanityApi.ParsedResponse.LeadInOffer,
) => {
  if (!leadInOffer) return undefined;
  return packageOptions.find((po) => po.id == leadInOffer.inclusionSetId);
};

export const parseAndSortCheapestOffers = (
  packageOffers: PostSearchPackageOptions,
  packageOptions: PackageOption[],
) => {
  const packageOptionsList: OtherPackageOffer[] = [];
  const { cheapestOffer, offers } = packageOffers;
  offers.forEach((offer) => {
    packageOptionsList.push({
      leadInOffer: offer?.leadInOffer,
      offer,
      packageOption: getPackageOption(packageOptions, offer?.leadInOffer),
    });
  });

  const sorted = packageOptionsList.sort(
    (a, b) => offerPrice(a.offer) - offerPrice(b.offer),
  );

  // We'll add the "Cheapest offer" to the bottom of the list, only if
  // it is cheaper than (and thus isn't), the cheapest Procured Offer.
  const isCheapestOfferDifferentToCheapestPO =
    sorted.length === 0 ||
    (sorted.length > 1 &&
      Number(sorted[0].offer?.charges.total.amount) >
        Number(cheapestOffer?.charges?.total?.amount));

  if (!!cheapestOffer?.charges && isCheapestOfferDifferentToCheapestPO) {
    sorted.push({
      leadInOffer: cheapestOffer?.leadInOffer,
      offer: cheapestOffer,
      packageOption: getPackageOption(
        packageOptions,
        cheapestOffer?.leadInOffer,
      ),
    });
  }

  return sorted;
};

export const sortedCheapestOffersPerPackageOption = ({
  roomOptions,
  packageOptions,
}: {
  roomOptions?: UI.RoomOption[];
  packageOptions: PackageOption[];
}) => {
  return parseAndSortCheapestOffers(
    getCheapestOfferPerPackageOption(roomOptions),
    packageOptions,
  );
};
